import { TableBody, TableRow, useMediaQuery } from '@material-ui/core';
import moment from 'moment';
import React from 'react';
import API from 'src/api/api';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';
import { APP_CONSTANT } from 'src/constants';
import { t } from 'src/i18n';
import {
  FixedLayoutTable,
  TableCellWrapper,
  TableHeadWrapper,
  useStyles
} from '../Dashboard.style';
import TableMobile, {
  TableMobileRow
} from '../../../components/TableMobile/TableMobile';

const UpcomingAppointment: React.FC = () => {
  const matches = useMediaQuery('(max-width:600px)');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [
    isFetchingUpcomingAppointmentFail,
    setIsFetchingUpcomingAppointmentFail
  ] = React.useState<boolean>(false);
  const [appointments, setListAppointments] = React.useState<any>([]);
  const classes = useStyles();

  React.useEffect(() => {
    const startDate = moment().toISOString();
    setIsLoading(true);

    API()
      .get(
        `/user/v1/family/appointments?includedMe=true&dateStart=${startDate}&withStatus=scheduled&withStatus=confirmed&limit=5`
      )
      .then((response) => {
        setListAppointments(response.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsFetchingUpcomingAppointmentFail(true);
        setIsLoading(false);
        setErrorMessage(t('upcomingAppointment.error'));
      });
  }, []);

  const formatDatetime = (time: string) => {
    return `${moment(time).format('MMM DD')} at ${moment(time).format(
      'hh:mm a'
    )}`;
  };

  const showProviders = (providers: []) => {
    if (providers && Array.isArray(providers)) {
      return providers.map((provider: any) => (
        <p key={provider.id}> {provider.name} </p>
      ));
    }

    return <></>;
  };

  const displayReasons = (description) => {
    if (description) {
      const section = description.split('\r\n');

      return section.map((s: string) => (
        <p key={s}>{s}</p>
      ));
    }
    
    return <></>;
  }

  const displayName = (firstName: string, lastName: string) => {
    return (
      <p>
        {' '}
        {firstName} {lastName}{' '}
      </p>
    );
  };

  const renderTable = (tableData: any) => {
    const mappedTableData =
      tableData.appointments &&
      tableData.appointments
        .map((appointment) => ({
          ...appointment,
          nameFirst: appointment.nameFirst,
          nameLast: appointment.nameLast
        }))
        .slice(0, APP_CONSTANT.UPCOMING_APPOINTMENTS_LIMIT);

    const mappedTableDataMobile: TableMobileRow[] = [];
    if (mappedTableData) {
      mappedTableData.forEach((item) => {
        mappedTableDataMobile.push({
          key: t('dashboard.appointment.table.patient'),
          value: `${item.nameFirst} ${item.nameLast}`
        });
        mappedTableDataMobile.push({
          key: t('dashboard.appointment.table.reason'),
          value: item.description,
          transformValue: (value): string | string[] => {
            if (typeof value === 'string') {
              return value.split('\r\n');
            }

            return value;
          }
        });
        mappedTableDataMobile.push({
          key: t('dashboard.appointment.table.appointment'),
          value: formatDatetime(item.dateTime)
        });
        let providers = [];
        if (Array.isArray(item.providers) && item.providers.length > 0) {
          providers = item.providers.map(
            (subItem: { id: string; name: string }): string => subItem.name
          );
        }
        mappedTableDataMobile.push({
          key: t('dashboard.appointment.table.provider'),
          value: providers
        });
      });
    }

    if (!mappedTableData || !mappedTableData.length) {
      return (
        <p className={`${classes.marginSide15} ${classes.text}`}>
          {t('upcomingAppointment.noData')}
        </p>
      );
    }

    if (isFetchingUpcomingAppointmentFail) {
      return (
        <p className={`${classes.messageError} ${classes.mb20} ${classes.mt15}`}>
          {' '}
          {t('upcomingAppointment.error')}{' '}
        </p>
      );
    }

    return !matches ? (
      <FixedLayoutTable>
        <TableHeadWrapper>
          <TableRow>
            <TableCellWrapper align="left">
              <p className={classes.tableHeader}>
                {t('dashboard.appointment.table.patient')}
              </p>
            </TableCellWrapper>
            <TableCellWrapper align="left">
              <p className={classes.tableHeader}>
                {t('dashboard.appointment.table.reason')}
              </p>
            </TableCellWrapper>
            <TableCellWrapper align="left">
              <p className={classes.tableHeader}>
                {t('dashboard.appointment.table.appointment')}
              </p>
            </TableCellWrapper>
            <TableCellWrapper align="left">
              <p className={classes.tableHeader}>
                {t('dashboard.appointment.table.provider')}
              </p>
            </TableCellWrapper>
          </TableRow>
        </TableHeadWrapper>
        <TableBody>
          {mappedTableData.map((row, index) => {
            return (
              <TableRow key={index} style={{ borderTop: '1px solid #E3EBF6' }}>
                <TableCellWrapper align="left" className={classes.tableCell}>
                  {displayName(row.nameFirst, row.nameLast)}
                </TableCellWrapper>
                <TableCellWrapper align="left" className={classes.tableCell}>
                  {displayReasons(row.description)}
                </TableCellWrapper>
                <TableCellWrapper align="left" className={classes.tableCell}>
                  {formatDatetime(row.dateTime)}
                </TableCellWrapper>
                <TableCellWrapper align="left" className={classes.tableCell}>
                  {showProviders(row.providers)}
                </TableCellWrapper>
              </TableRow>
            );
          })}
        </TableBody>
      </FixedLayoutTable>
    ) : (
      <TableMobile data={mappedTableDataMobile} />
    );
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      {appointments && renderTable(appointments)}
    </LoadingContainer>
  );
};

export default UpcomingAppointment;
